import bgLocale from "@fullcalendar/core/locales/bg";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import DeleteIcon from "@mui/icons-material/Delete";
import { Box, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { Button, ReferenceField, TextField, useRecordContext } from "react-admin";
import permissions from "app/permissions";
import SecuredComponent from "server/security/SecuredComponent";
import ConfirmModal from "server/components/Modals/ConfirmModal";
import dataProvider from "server/dataProvider";
import { doctoralRequests } from "api/doctoralApi";
import { administrationRequests } from "api/administrationApi";
import { employeeRequests } from "api/employeeApi";
import { useNotifyError } from "utils/useNotifyError";
import { DoctoralSchoolCampaignStatus } from "utils/constants";
import ClassDialog from "./ClassDialog";
import { formatDoctoralDate } from "../../Partials/DoctoralDateField";

const ClassesSection = () => {
  const campaign = useRecordContext();
  const notifyError = useNotifyError();
  const [classes, setClasses] = useState([]);

  const campaignId = campaign?.id;

  const load = useCallback(async () => {
    if (!campaignId) return;
    try {
      const { data } = await dataProvider.customRequest(
        doctoralRequests.doctoralSchoolClasses(campaignId),
        null,
        "GET",
      );
      setClasses(data ?? []);
    } catch (err) {
      notifyError(err);
      setClasses([]);
    }
  }, [campaignId, notifyError]);

  useEffect(() => {
    load();
  }, [load]);

  if (!campaign) return null;

  const isCompleted = campaign.status === DoctoralSchoolCampaignStatus.Completed;

  const events = classes.map(item => ({
    id: `${item.id}`,
    title: item.topic,
    start: item.startsAt,
    end: item.endsAt,
  }));

  const handleDelete = async classId => {
    try {
      await dataProvider.customRequest(
        doctoralRequests.doctoralSchoolClass(campaignId, classId),
        null,
        "DELETE",
      );
      await load();
    } catch (err) {
      notifyError(err);
    }
  };

  return (
    <Box>
      {!isCompleted && (
        <SecuredComponent permission={permissions.canUpdateDoctoralSchoolCampaigns}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
            <ClassDialog
              campaignId={campaignId}
              onSaved={load}
            />
          </Box>
        </SecuredComponent>
      )}

      {classes.length === 0 ? (
        <Typography
          variant="body2"
          sx={{ textAlign: "center", color: "text.secondary", py: 2 }}
        >
          Няма въведени занятия за това докторантско училище.
        </Typography>
      ) : (
        <>
          <Box sx={{ height: 600, mb: 3 }}>
            <FullCalendar
              plugins={[timeGridPlugin, dayGridPlugin]}
              initialView="timeGridWeek"
              headerToolbar={{
                left: "prev today next",
                center: "title",
                right: "dayGridMonth timeGridWeek",
              }}
              initialDate={classes[0]?.startsAt}
              slotMinTime="07:00:00"
              slotMaxTime="21:00:00"
              locale={bgLocale}
              firstDay={1}
              allDaySlot={false}
              height="100%"
              events={events}
            />
          </Box>

          {classes.map(item => (
            <Box
              key={item.id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
                py: 0.5,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography
                variant="body2"
                sx={{ minWidth: 280 }}
              >
                {formatDoctoralDate(item.startsAt, true)} — {formatDoctoralDate(item.endsAt, true)}
              </Typography>
              <Typography
                variant="body2"
                sx={{ flexGrow: 1 }}
              >
                {item.topic}
              </Typography>
              <ReferenceField
                record={item}
                source="lecturerEmployeeId"
                reference={employeeRequests.getProfessorsSelect()}
                link={false}
              >
                <TextField source="lastName" />
              </ReferenceField>
              <ReferenceField
                record={item}
                source="roomId"
                reference={administrationRequests.getRoomsSelect()}
                link={false}
              >
                <TextField source="roomName" />
              </ReferenceField>
              {!isCompleted && (
                <SecuredComponent permission={permissions.canUpdateDoctoralSchoolCampaigns}>
                  <ClassDialog
                    campaignId={campaignId}
                    schoolClass={item}
                    onSaved={load}
                  />
                  <ConfirmModal
                    title="Изтриване на занятие"
                    content="Сигурни ли сте, че искате да изтриете това занятие?"
                    onConfirm={() => handleDelete(item.id)}
                    triggerButton={
                      <Button
                        label=""
                        startIcon={<DeleteIcon />}
                      />
                    }
                  />
                </SecuredComponent>
              )}
            </Box>
          ))}
        </>
      )}
    </Box>
  );
};

export default ClassesSection;
